import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const IMG_HERO = "/brand/home-hero.jpg";

const MARQUEE = [
  "Made in The Bahamas",
  "Thoughtful Storytelling",
  "Original Artwork",
  "Premium Paper Goods",
  "Designed to Be Kept",
  "Beautiful Keepsakes",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [featuredPrints, setFeaturedPrints] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true&limit=3").then(r => setFeatured(r.data)).catch(() => {});
    // Curated print picks — fetched by slug so this list is easy to edit later.
    const printSlugs = ["print-dock-boys", "print-caviar", "print-xuma-piggies"];
    Promise.all(printSlugs.map(s => api.get(`/products/${s}`).then(r => r.data.product).catch(() => null)))
      .then(list => setFeaturedPrints(list.filter(Boolean)));
  }, []);

  return (
    <div data-testid="home-page" className="fade-up">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-6 md:pt-10 pb-10 md:pb-12">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
            <div className="md:col-span-6">
              <p className="overline hidden" data-testid="home-eyebrow">A Bahamian Design Studio</p>
              <h1
                data-testid="home-hero-title"
                className="font-serif font-light text-brand-dark leading-[1.05] tracking-tight text-3xl sm:text-4xl md:text-[54px] mt-4"
              >
                Bringing a little<br />
                more <span className="italic text-brand-accent">Bahamas</span><br />
                home.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-brand-neutral leading-relaxed max-w-lg font-light">
                This is our love letter to The Bahamas. Drawing inspiration
                from our people, traditions, humour and everyday moments to
                create premium paper products.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/products" data-testid="hero-shop-cta" className="btn-primary w-64 justify-between whitespace-nowrap">
                  <span>Shop the Studio</span>
                  <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                </Link>
                <Link to="/services" data-testid="hero-services-cta" className="btn-outline w-64 justify-between whitespace-nowrap">
                  <span>Design Services</span>
                  <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="md:col-span-6 md:col-start-7 relative">
              <div className="img-zoom w-full max-w-[600px] aspect-[600/390] overflow-hidden ml-auto">
                <img src={IMG_HERO} alt="Sketchbook page with hand-drawn Bahamian fish and shells" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* MARQUEE */}
        <div className="bg-brand-surface border-y border-brand-border py-4 overflow-hidden">
          <div className="marquee-track">
            {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((t, i) => (
              <span key={i} className="font-serif italic text-lg text-brand-accent px-8 whitespace-nowrap">
                {t} <span className="text-brand-border mx-2">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between gap-6 mb-14">
          <div>
            <p className="overline hidden">The Collection</p>
            <h2 className="font-serif font-light text-2xl md:text-3xl mt-4 text-brand-dark tracking-tight">
              Worth Sending, <span className="italic">Maybe Keeping</span>
            </h2>
          </div>
          <Link to="/products" data-testid="home-view-all" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest text-brand-accent hover:text-brand-accent-hover">
            View all <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-16 gap-x-8">
          {featured.slice(0, 3).map((p) => (
            <ProductCard key={p.slug} product={p} testIdPrefix="featured-product" overlineText={p.occasion} />
          ))}
        </div>
      </section>

      {/* FEATURED PRINTS */}
      <section className="bg-brand-surface paper-texture py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline text-brand-accent">Fine Art</p>
              <h2 className="font-serif font-light text-3xl md:text-5xl mt-3 text-brand-dark leading-tight">
                Made to <span className="italic">hang</span>, not just to hold.
              </h2>
              <p className="mt-4 text-brand-neutral max-w-lg leading-relaxed">
                Signed, limited-edition prints from the studio — printed on premium archival paper.
              </p>
            </div>
            <Link to="/prints" data-testid="home-view-prints" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest text-brand-accent hover:text-brand-dark">
              Shop All Prints <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-16 gap-x-8">
            {featuredPrints.map((p) => (
              <ProductCard key={p.slug} product={p} testIdPrefix="featured-print" />
            ))}
          </div>
          <div className="md:hidden text-center mt-10">
            <Link to="/prints" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-brand-accent">
              Shop All Prints <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GIFT CARDS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="overline text-brand-accent">Gift Cards</p>
            <h2 className="font-serif font-light text-3xl md:text-5xl mt-3 text-brand-dark leading-tight">
              Share the <span className="italic">love</span>.
            </h2>
            <p className="mt-5 text-brand-neutral max-w-lg leading-relaxed">
              For bespoke illustrations, custom stationery, original artwork — or simply to let
              them choose their favourite greeting cards or limited-edition prints.
            </p>
            <Link
              to="/gift-cards"
              data-testid="home-giftcards-cta"
              className="inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-widest text-brand-accent hover:text-brand-dark border-b border-brand-accent pb-1"
            >
              Send a Gift Card <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
          <Link
            to="/gift-cards"
            className="md:col-span-6 order-1 md:order-2 block img-zoom overflow-hidden"
            data-testid="home-giftcards-image"
          >
            <img
              src="/brand/gift-card.png"
              alt="The Island Studio gift card"
              className="w-full h-auto object-cover"
            />
          </Link>
        </div>
      </section>

      {/* WHOLESALE */}
      <section className="bg-brand-dark text-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <p className="overline text-white/60">Wholesale</p>
              <h2 className="font-serif font-light text-4xl md:text-6xl mt-4 leading-tight">
                For shops that share<br /> our love of <span className="italic">paper</span>.
              </h2>
              <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
                For shops, boutiques and hotels — register to stock The Island Studio.
                Minimums start at 25 units; bespoke collections and pre-orders welcomed.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/wholesale" data-testid="home-wholesale-cta" className="btn-ghost-dark">
                Shop Wholesale <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
